import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { scrollToId } from "@/lib/scroll";

export const LogoMark = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <defs>
      <linearGradient id="jgA" x1="0" y1="1" x2="0.6" y2="0">
        <stop offset="0" stopColor="#144A9E" />
        <stop offset="1" stopColor="#4DA6FF" />
      </linearGradient>
      <linearGradient id="jgB" x1="0" y1="1" x2="0.6" y2="0">
        <stop offset="0" stopColor="#1B5CB8" />
        <stop offset="1" stopColor="#3D9EFF" />
      </linearGradient>
      <linearGradient id="jgC" x1="0" y1="1" x2="0.7" y2="0">
        <stop offset="0" stopColor="#1B5CB8" />
        <stop offset="1" stopColor="#5FB4FF" />
      </linearGradient>
    </defs>
    <rect x="5" y="27" width="10" height="16" rx="5" fill="url(#jgA)" stroke="#0E100F" strokeWidth="2" />
    <rect x="19" y="19" width="10" height="24" rx="5" fill="url(#jgB)" stroke="#0E100F" strokeWidth="2" />
    <rect x="33" y="5" width="10" height="38" rx="5" fill="url(#jgC)" stroke="#0E100F" strokeWidth="2" />
    <rect x="36.5" y="9" width="3" height="14" rx="1.5" fill="#E9FFEC" opacity="0.5" />
  </svg>
);

const LangToggle = ({ dark = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center gap-1.5" data-testid="lang-switcher">
      {["de", "en"].map((l, i) => (
        <span key={l} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-bone/25">|</span>}
          <button
            data-testid={`lang-switcher-${l}`}
            onClick={() => setLang(l)}
            className={`font-mono text-xs uppercase tracking-widest transition-colors ${
              lang === l
                ? "text-gold"
                : dark
                  ? "text-bone/50 hover:text-bone"
                  : "text-bone/40 hover:text-bone"
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

const Logo = () => (
  <span className="flex items-center gap-3 text-bone">
    <LogoMark />
    <span className="flex flex-col items-start leading-none">
      <span className="font-display text-lg font-bold tracking-[0.3em]">JOUDI</span>
      <span className="mt-1 font-mono text-[8px] uppercase tracking-[0.5em] text-gold">Group</span>
    </span>
  </span>
);

const Navigation = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [
    { id: "top", label: t.nav.home },
    { id: "leistungen", label: t.nav.services },
    { id: "eroeffnung", label: t.nav.eroeffnung },
    { id: "prozess", label: t.nav.process },
    { id: "branchen", label: t.nav.industries },
    { id: "pakete", label: t.nav.packages },
    { id: "faq", label: t.nav.faq },
  ];

  const go = (id) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        scrollToId(id);
      } else {
        navigate("/");
        setTimeout(() => scrollToId(id), 500);
      }
    }, open ? 150 : 0);
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-bone/10 bg-dark/85 backdrop-blur-md">
        <nav className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-6 lg:px-16">
          <button data-testid="nav-logo" onClick={() => go("top")}>
            <Logo />
          </button>
          <div className="hidden items-center gap-8 lg:flex">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="link-underline text-sm font-medium text-bone/70 transition-colors hover:text-bone"
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-5">
            <LangToggle />
            <button
              data-testid="nav-cta"
              onClick={() => go("kontakt")}
              className="group hidden items-center gap-2 rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone lg:inline-flex"
            >
              {t.nav.cta}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="mobile-menu-btn"
              aria-label={t.nav.menuOpen}
              onClick={() => setOpen(true)}
              className="inline-flex rounded-[3px] border border-bone/20 p-2.5 text-bone lg:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy px-6 py-6 text-bone"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                aria-label={t.nav.menuClose}
                onClick={() => setOpen(false)}
                className="rounded-[3px] border border-bone/25 p-2.5"
              >
                <X size={18} />
              </button>
            </div>
            <div className="mt-14 flex flex-col gap-6">
              {[...links, { id: "kontakt", label: t.nav.contact }].map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-4xl"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <div className="mt-auto flex items-center justify-between">
              <LangToggle dark />
              <span className="font-mono text-xs uppercase tracking-widest text-bone/50">Paphos · CY</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
